/**
 * NoteEditor — full clinical note editor with voice dictation + AI scribe.
 *
 * Section-by-section text entry (template-driven), browser Web Speech API
 * voice dictation, Bedrock AI scribe that structures dictation into the
 * template sections, smart-phrase expansion, ICD-10 + CPT entry, sign &
 * finalize flow with diagnosis-code validation, addendum flow on signed
 * notes.
 *
 * Verbiage per the Carlos demo spec (2026-06-07). Web Speech API used
 * client-side for voice transcription in v1 (Chrome/Edge/Safari); AWS
 * Transcribe Medical wiring deferred to next session.
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import useDictation from "../../lib/useDictation";
import {
  Mic, MicOff, Sparkles, FileText, Save, FileCheck2, Lock, ChevronLeft,
  AlertCircle, CheckCircle2, Loader2, Plus, X, Edit3, Pause, Play, Shield,
  Printer, Download, UserCheck, RefreshCcw,
} from "lucide-react";
import MedicationSafetyPanel from "../../components/MedicationSafetyPanel";
import { useNavGuard } from "../../lib/NavHistory";

const STATUS_BADGES = {
  draft:   { bg: "#fef6e0", color: "#7c5210", border: "#e0b973", label: "Draft" },
  signed:  { bg: "#e9f5e9", color: "#2d6b2d", border: "#a5d5a5", label: "Signed" },
  locked:  { bg: "#e8f0fb", color: "#1f6feb", border: "#c9d6e8", label: "Locked" },
  amended: { bg: "#f4e8fb", color: "#6b1f8b", border: "#d6c9e8", label: "Amended" },
};

const AUTOSAVE_INTERVAL = 30000;   // "Auto-Save Interval (every 30 seconds)" per spec

export default function NoteEditor({ noteId, user, onBack }) {
  const [note, setNote] = useState(null);
  const [template, setTemplate] = useState(null);
  const [sections, setSections] = useState({});
  const [icd10Codes, setIcd10Codes] = useState([]);
  const [cptCodes, setCptCodes] = useState([]);
  const [smartPhrases, setSmartPhrases] = useState([]);
  const [addenda, setAddenda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ kind: "idle", text: "" });
  // #65 Phase B2: edits since the last successful save. Drives useNavGuard so
  // Back / hardware-back / sidebar prompt before abandoning unsaved note text.
  const [dirty, setDirty] = useState(false);
  const [activeSectionKey, setActiveSectionKey] = useState(null);

  // Dictation state
  const [dictating, setDictating] = useState(false);
  const [dictationPaused, setDictationPaused] = useState(false);
  const [dictationStatus, setDictationStatus] = useState("idle");  // idle | listening | processing | complete
  const [dictationBuffer, setDictationBuffer] = useState("");
  const [dictationTargetSection, setDictationTargetSection] = useState(null);
  /** Caret position inside the active section, reported by its textarea. */
  const cursorRef = useRef(null);
  const [aiScribing, setAiScribing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [showAiReviewModal, setShowAiReviewModal] = useState(false);

  // Addendum
  const [showAddendumModal, setShowAddendumModal] = useState(false);
  const [addendumText, setAddendumText] = useState("");
  const [addendumReason, setAddendumReason] = useState("");

  // ICD-10 / CPT input fields
  const [newIcd, setNewIcd] = useState("");
  const [newCpt, setNewCpt] = useState("");

  // Medication safety check
  const [showSafetyCheck, setShowSafetyCheck] = useState(false);
  const [safetyForm, setSafetyForm] = useState({ drugName: "", dosage: "", frequency: "", route: "oral" });
  const [safetyProposed, setSafetyProposed] = useState(null);

  // Route for review
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [routeTargetUserId, setRouteTargetUserId] = useState("");
  const [routeNotes, setRouteNotes] = useState("");
  const [reviewers, setReviewers] = useState([]);
  const [exporting, setExporting] = useState(false);

  const autosaveTimerRef = useRef(null);
  const sectionsRef = useRef({});

  const canEdit = (user && user.permissions && user.permissions.includes("notes:update"));
  const canSign = (user && user.permissions && user.permissions.includes("notes:sign"));

  // #65 Phase B2: prompt before leaving a draft with unsaved edits. Generic
  // message only — never the patient name or note content (PHI stays out of
  // the nav model). No-op once saved/signed (dirty=false) or when read-only.
  useNavGuard({
    when: dirty && canEdit && note && note.status === "draft",
    message: "You have unsaved changes to this note. Leave without saving?",
  });
  const canCreate = (user && user.permissions && user.permissions.includes("notes:create"));

  // ── Load ──────────────────────────────────────────────────────────────
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r, tpls, phrases] = await Promise.all([
        window.zignagenixAPI.invoke("notes:get", noteId),
        window.zignagenixAPI.invoke("notes:templates:list"),
        window.zignagenixAPI.invoke("notes:smart-phrases:list"),
      ]);
      if (r && r.error) { setError(r.message || r.code); setLoading(false); return; }
      setNote(r.note);
      setAddenda(r.addenda || []);
      setSections(r.note.sections || {});
      sectionsRef.current = r.note.sections || {};
      setIcd10Codes(r.note.icd10_codes || []);
      setCptCodes(r.note.cpt_codes || []);
      const tpl = (Array.isArray(tpls) ? tpls : []).find((t) => t.template_key === r.note.template_key);
      setTemplate(tpl || null);
      if (tpl && tpl.section_keys && tpl.section_keys.length) {
        setActiveSectionKey(tpl.section_keys[0]);
      }
      setSmartPhrases(Array.isArray(phrases) ? phrases : []);
    } catch (e) {
      setError((e && e.message) || "Failed to load note");
    }
    setLoading(false);
  }, [noteId]);

  useEffect(() => { reload(); }, [reload]);

  // ── Auto-save (every 30s, only when draft + dirty) ────────────────────
  // saveDraft is intentionally NOT in deps — it captures sectionsRef.current
  // each tick, which is what we want. Including it would recreate the timer
  // on every state change and reset the interval mid-tick.
  const noteStatus = note ? note.status : null;
  useEffect(() => {
    if (!noteStatus || noteStatus !== "draft" || !canEdit) return undefined;
    const timer = setInterval(() => {
      window.zignagenixAPI.invoke("notes:update-draft", {
        id: noteId,
        sections: sectionsRef.current,
      }).catch(() => {});
    }, AUTOSAVE_INTERVAL);
    autosaveTimerRef.current = timer;
    return () => clearInterval(timer);
  }, [noteStatus, canEdit, noteId]);

  // ── Save draft ────────────────────────────────────────────────────────
  async function saveDraft({ silent } = {}) {
    if (!note || note.status !== "draft") return;
    if (!silent) setSaveStatus({ kind: "saving", text: "Saving…" });
    try {
      const resp = await window.zignagenixAPI.invoke("notes:update-draft", {
        id: noteId,
        sections: sectionsRef.current,
        icd10Codes,
        cptCodes,
      });
      if (resp && resp.error) {
        setSaveStatus({ kind: "error", text: resp.message || resp.code });
        return;
      }
      setSaveStatus({ kind: "ok", text: `Saved at ${new Date().toLocaleTimeString()}` });
      setDirty(false);   // #65 Phase B2: persisted — clear the unsaved-changes guard
    } catch (e) {
      setSaveStatus({ kind: "error", text: (e && e.message) || "Save failed" });
    }
  }

  // ── Update a section's text ───────────────────────────────────────────
  function updateSection(key, value) {
    const expanded = expandSmartPhrases(value, smartPhrases);
    setDirty(true);   // #65 Phase B2: unsaved edit → arm the nav guard
    setSections((prev) => {
      const next = { ...prev, [key]: expanded };
      sectionsRef.current = next;
      return next;
    });
  }

  /*
   * ── ⛔⛔ WEB SPEECH REMOVED — DANIEL'S RULING, 30 Oct 2026 (Card P183) ─────
   *
   * This dictated CLINICAL NOTE CONTENT through
   * window.SpeechRecognition / webkitSpeechRecognition, which streams the
   * audio off-device — Chrome to Google, Safari and iOS to Apple. Neither is
   * under our BAA.
   *
   * ⚠⚠ THE CARD NAMED ONLY THE INTAKE MODAL. This second surface was found by
   * grepping the tree and is the MORE sensitive of the two: intake dictation
   * carries a prospect's contact details, this one carries the note itself.
   * Removing one and leaving the other would have closed the smaller hole and
   * reported the job done.
   *
   * Typing is unaffected — every section is a textarea and always has been.
   * The dictation controls are DISABLED rather than deleted so a clinician
   * sees where the feature went; the buffer/insert/AI-scribe plumbing below is
   * left intact for PR 2 to feed from Transcribe Medical.
   */
  /*
   * ⭐ P193 — dictation restored, on the SAME Transcribe Medical batch path the
   * CEO report and CRM intake use (routes/dictation.js, purpose "note",
   * prefix note-dictation/). The browser speech API #762 removed is NOT coming
   * back: this records with getUserMedia + MediaRecorder and the audio goes to
   * our backend, inside the BAA.
   *
   * ⚠ Specialty is PRIMARYCARE. Transcribe Medical offers PRIMARYCARE,
   * CARDIOLOGY, NEUROLOGY, ONCOLOGY, RADIOLOGY and UROLOGY; the clinic is a
   * general cell-therapy practice and
   * docs/transcribe_medical_design_2026-06-07.md section 6 records specialty
   * mapping as an open question for Daniel and Carlos. PRIMARYCARE is the
   * documented default, not a ruling made here.
   */
  const noteMic = useDictation({
    send: (payload) => (window.zignagenixAPI && window.zignagenixAPI.dictateClinicalNote
      ? window.zignagenixAPI.dictateClinicalNote(payload)
      : Promise.resolve({ error: true, message: "Dictation is unavailable in this build." })),
    onTranscript: (t) => {
      if (!t) { setDictationStatus("idle"); setDictating(false); return; }
      /*
       * ⭐ INSERTED AT THE CARET, and EDITABLE — it is a textarea, it always
       * was. Nothing is saved: the note still has to be saved explicitly, and
       * a draft stays a draft.
       */
      const key = dictationTargetSection || activeSectionKey;
      if (!key) { setDictationBuffer(t); setDictationStatus("complete"); return; }
      const current = sections[key] || "";
      const at = Number.isInteger(cursorRef.current)
        ? Math.max(0, Math.min(cursorRef.current, current.length))
        : current.length;
      const before = current.slice(0, at);
      const after = current.slice(at);
      const sep = before && !/\s$/.test(before) ? " " : "";
      updateSection(key, `${before}${sep}${t}${after}`);
      setDictationBuffer(t);
      setDictationStatus("complete");
      setDictating(false);
    },
  });

  function startDictation(sectionKey) {
    const key = sectionKey || activeSectionKey;
    if (!key) { setError("Pick a section before dictating."); return; }
    setDictationTargetSection(key);
    setDictationBuffer("");
    setError(null);
    if (noteMic.state === 'recording') { noteMic.stop(); return; }
    setDictating(true);
    setDictationStatus("listening");
    noteMic.start();
  }

  /*
   * ⚠ PAUSE/RESUME ARE NOT SUPPORTED ON THE BATCH PATH, and saying so beats
   * pretending. Transcribe Medical batch takes one clip per job; a pause would
   * mean stitching clips, which is a streaming feature. Stopping ends the clip
   * and transcribes it; starting again appends a second insertion at the
   * caret. The controls below are therefore not offered.
   */
  function pauseDictation() {
    noteMic.stop();
    setDictationPaused(true);
    setDictationStatus("idle");
  }

  function resumeDictation() {
    // Resume = start a fresh recognition, appending to the existing buffer
    if (!dictationTargetSection) return;
    const existing = dictationBuffer;
    startDictation(dictationTargetSection);
    setDictationBuffer(existing);   // restore
  }

  function stopAndInsert() {
    // ⚠ The transcript arrives asynchronously via the hook's onTranscript and
    // is inserted there. This only ends the recording.
    noteMic.stop();
    setDictationStatus("processing");
  }

  function cancelDictation() {
    noteMic.stop();
    setDictating(false);
    setDictationStatus("idle");
    setDictationBuffer("");
    setDictationTargetSection(null);
  }

  function insertDictationIntoSection() {
    if (!dictationTargetSection || !dictationBuffer) return;
    const current = sections[dictationTargetSection] || "";
    const joined = current ? `${current}\n${dictationBuffer}` : dictationBuffer;
    updateSection(dictationTargetSection, joined);
    cancelDictation();
  }

  // ── AI Scribe ────────────────────────────────────────────────────────
  async function runAiScribe() {
    if (!dictationBuffer || dictationBuffer.length < 10) {
      setError("Dictate more content before running AI scribe.");
      return;
    }
    setAiScribing(true);
    setError(null);
    try {
      const resp = await window.zignagenixAPI.invoke("notes:ai-structure-from-dictation", {
        templateKey: note.template_key,
        dictation: dictationBuffer,
        patientId: note.patient_id,
      });
      if (resp && resp.error) {
        setError(resp.message || resp.code);
        setAiScribing(false);
        return;
      }
      setAiResult(resp);
      setShowAiReviewModal(true);
    } catch (e) {
      setError((e && e.message) || "AI scribe failed");
    }
    setAiScribing(false);
  }

  function acceptAiResult() {
    if (!aiResult || !aiResult.sections) return;
    const merged = { ...sectionsRef.current };
    for (const [k, v] of Object.entries(aiResult.sections)) {
      if (v && v.trim()) {
        // Append to existing content rather than replace, so the physician
        // doesn't lose any pre-existing text in that section
        merged[k] = merged[k] ? `${merged[k]}\n\n${v}` : v;
      }
    }
    setSections(merged);
    sectionsRef.current = merged;
    setShowAiReviewModal(false);
    setAiResult(null);
    cancelDictation();
  }

  // ── Sign & finalize ──────────────────────────────────────────────────
  async function signAndFinalize() {
    if (!canSign) return;
    if (!icd10Codes.length) {
      setError("Note must contain at least one diagnosis code before finalization.");
      return;
    }
    // Save the latest draft state first
    await saveDraft({ silent: true });
    if (!window.confirm("Sign and finalize this note? It will become immutable. You can still add an addendum later if a correction is needed.")) return;
    try {
      const resp = await window.zignagenixAPI.invoke("notes:sign-and-finalize", { id: noteId });
      if (resp && resp.error) { setError(resp.message || resp.code); return; }
      await reload();
    } catch (e) {
      setError((e && e.message) || "Sign failed");
    }
  }

  async function lockNote() {
    if (!window.confirm("Lock this note as co-signed? It will become permanently locked.")) return;
    try {
      const resp = await window.zignagenixAPI.invoke("notes:lock", { id: noteId });
      if (resp && resp.error) { setError(resp.message || resp.code); return; }
      await reload();
    } catch (e) {
      setError((e && e.message) || "Lock failed");
    }
  }

  async function submitAddendum() {
    if (addendumText.trim().length < 5) {
      setError("Addendum text must be at least 5 characters.");
      return;
    }
    try {
      const resp = await window.zignagenixAPI.invoke("notes:addendum:create", {
        noteId,
        text: addendumText.trim(),
        reason: addendumReason.trim() || null,
      });
      if (resp && resp.error) { setError(resp.message || resp.code); return; }
      setShowAddendumModal(false);
      setAddendumText("");
      setAddendumReason("");
      await reload();
    } catch (e) {
      setError((e && e.message) || "Addendum failed");
    }
  }

  // ── Print Note ─────────────────────────────────────────────────────
  function printNote() {
    if (!note || !template) return;
    // Build a print-friendly HTML doc and open it via a Blob URL. We
    // deliberately avoid `document.write` (XSS static-check policy)
    // even though every value here is escaped — Blob URL is cleaner
    // and triggers window.print() on load via an embedded onload hook.
    const html = buildPrintHtml({ note, template, sections, icd10Codes, cptCodes, addenda });
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=900,height=1100");
    if (!w) {
      URL.revokeObjectURL(url);
      setError("Browser blocked the print window. Allow pop-ups for portal.zignagenix.com and try again.");
      return;
    }
    // Revoke after a generous delay so the browser has time to render
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  // ── Export PDF (server-rendered for HIPAA-grade reproducibility) ───
  async function exportPdf() {
    if (!note) return;
    setExporting(true);
    setError(null);
    try {
      const resp = await window.zignagenixAPI.invoke("notes:export-pdf", noteId);
      if (resp && resp.error) {
        setError(resp.message || resp.code || "Export failed");
        setExporting(false);
        return;
      }
      // Convert base64 → Blob → download
      const bin = atob(resp.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: resp.contentType || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = resp.filename || `clinical_note_${noteId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError((e && e.message) || "Export failed");
    }
    setExporting(false);
  }

  // ── Route for Review ──────────────────────────────────────────────
  async function openRouteModal() {
    setShowRouteModal(true);
    if (reviewers.length === 0) {
      try {
        const list = await window.zignagenixAPI.invoke("db:users:list");
        const physicians = (Array.isArray(list) ? list : [])
          .filter((u) => ["physician", "admin", "sysadmin"].includes(String(u.role || "").toLowerCase()))
          .filter((u) => u.id !== (user && user.id));
        setReviewers(physicians);
      } catch (_) { /* tolerated */ }
    }
  }
  async function submitRoute() {
    const id = parseInt(routeTargetUserId, 10);
    if (!id) { setError("Pick a reviewer first."); return; }
    try {
      const resp = await window.zignagenixAPI.invoke("notes:route-for-review", {
        id: noteId, targetUserId: id, notes: routeNotes.trim() || null,
      });
      if (resp && resp.error) { setError(resp.message || resp.code); return; }
      setShowRouteModal(false);
      setRouteTargetUserId("");
      setRouteNotes("");
      await reload();
    } catch (e) {
      setError((e && e.message) || "Route failed");
    }
  }

  // ── Re-Dictate Section ────────────────────────────────────────────
  function reDictateSection() {
    if (!activeSectionKey) return;
    if (!window.confirm(`Re-dictate the "${(template.section_labels && template.section_labels[activeSectionKey]) || activeSectionKey}" section? Current content will be replaced.`)) return;
    updateSection(activeSectionKey, "");
    startDictation(activeSectionKey);
  }

  // ── ICD-10 / CPT management ──────────────────────────────────────────
  function addIcd() {
    const v = newIcd.trim().toUpperCase();
    if (!v) return;
    if (icd10Codes.includes(v)) { setNewIcd(""); return; }
    setIcd10Codes([...icd10Codes, v]);
    setNewIcd("");
  }
  function addCpt() {
    const v = newCpt.trim();
    if (!v) return;
    if (cptCodes.includes(v)) { setNewCpt(""); return; }
    setCptCodes([...cptCodes, v]);
    setNewCpt("");
  }

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) return <div className="page-loading">Loading note…</div>;
  if (!note || !template) return <div className="page-loading">Note not found.</div>;

  const isDraft = note.status === "draft";
  const isSigned = note.status === "signed" || note.status === "locked" || note.status === "amended";
  const sb = STATUS_BADGES[note.status] || STATUS_BADGES.draft;

  return (
    <div className="page" style={{ padding: "16px 24px", maxWidth: 1280, margin: "0 auto" }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <button onClick={onBack} className="btn-ghost" style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <ChevronLeft size={14} /> Back
        </button>
        <h1 style={{ margin: 0, fontSize: 20 }}>{template.name}</h1>
        <span style={{ ...statusBadge, background: sb.bg, color: sb.color, border: `1px solid ${sb.border}` }}>
          {sb.label}
        </span>
        {note.signed_at && (
          <span style={{ fontSize: 11, color: "#5a6478" }}>
            Signed by {note.signed_by_display_name} at {new Date(note.signed_at).toLocaleString()}
          </span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {isDraft && canEdit && (
            <button onClick={() => saveDraft()} className="btn-ghost">
              <Save size={14} /> Save as Draft
            </button>
          )}
          {isDraft && canSign && (
            <button onClick={signAndFinalize} className="btn-primary">
              <FileCheck2 size={14} /> Sign & Finalize Note
            </button>
          )}
          {note.status === "signed" && canSign && (
            <button onClick={lockNote} className="btn-ghost">
              <Lock size={14} /> Sign & Lock
            </button>
          )}
          {isSigned && canCreate && (
            <button onClick={() => setShowAddendumModal(true)} className="btn-ghost">
              <Edit3 size={14} /> Addendum
            </button>
          )}
          {/* ── Route for Review ─ available on draft notes (forward to supervising physician / billing) */}
          {isDraft && canEdit && (
            <button onClick={openRouteModal} className="btn-ghost" title="Forward to a supervising physician or billing for review">
              <UserCheck size={14} /> Route for Review
            </button>
          )}
          {/* ── Print Note ─ always available */}
          <button onClick={printNote} className="btn-ghost" title="Open print-friendly view (also lets you Save as PDF from print dialog)">
            <Printer size={14} /> Print
          </button>
          {/* ── Export PDF ─ server-rendered for HIPAA-grade reproducibility */}
          <button onClick={exportPdf} disabled={exporting} className="btn-ghost" title="Download a server-rendered PDF with full e-signature provenance">
            {exporting ? <><Loader2 size={14} className="spin" /> Exporting…</> : <><Download size={14} /> Export PDF</>}
          </button>
        </div>
      </div>

      {/* ── Save status / errors ─────────────────────────────── */}
      {saveStatus.kind !== "idle" && (
        <div style={{ ...statusBar, ...statusBarColor(saveStatus.kind) }}>
          {saveStatus.kind === "saving" && <Loader2 size={12} className="spin" />}
          {saveStatus.kind === "ok" && <CheckCircle2 size={12} />}
          {saveStatus.kind === "error" && <AlertCircle size={12} />}
          {saveStatus.text}
        </div>
      )}
      {error && (
        <div style={{ ...statusBar, ...statusBarColor("error") }}>
          <AlertCircle size={12} /> {error}
        </div>
      )}

      {/* ── SOAP reminder + compliance tooltip ─────────────────── */}
      {note.template_key === "SOAP" && (
        <div style={tooltipBar}>
          <strong>SOAP Format Reminder:</strong> Subjective → Objective → Assessment → Plan
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 18 }}>

        {/* ── Section sidebar ────────────────────────────────── */}
        <div style={{ background: "#f9fafc", border: "1px solid #e1e6ed", borderRadius: 4, padding: 8 }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", color: "#5a6478", padding: "4px 8px", letterSpacing: 0.4 }}>Sections</div>
          {(template.section_keys || []).map((k) => {
            const isActive = activeSectionKey === k;
            const hasContent = (sections[k] || "").trim().length > 0;
            return (
              <button
                key={k}
                onClick={() => setActiveSectionKey(k)}
                style={{
                  width: "100%", display: "block", textAlign: "left",
                  padding: "6px 10px", margin: "2px 0", fontSize: 12,
                  background: isActive ? "#fff" : "transparent",
                  color: isActive ? "#1f6feb" : "#3a4a5e",
                  border: `1px solid ${isActive ? "#c9d6e8" : "transparent"}`,
                  borderRadius: 3, cursor: "pointer",
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {(template.section_labels && template.section_labels[k]) || k}
                {hasContent && (
                  <span style={{ float: "right", color: "#2d6b2d", fontSize: 10 }}>●</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Section editor ─────────────────────────────────── */}
        <div>

          {/* ── Medication safety affordance ───────────────────── */}
          {isDraft && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => setShowSafetyCheck(!showSafetyCheck)}
                style={{
                  padding: "6px 12px", fontSize: 12, fontWeight: 600,
                  background: showSafetyCheck ? "#1f6feb" : "#fff",
                  color: showSafetyCheck ? "#fff" : "#1f6feb",
                  border: "1px solid #1f6feb", borderRadius: 3, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Shield size={13} />
                {showSafetyCheck ? "Hide medication safety check" : "Check medication safety (contraindications, allergies, interactions)"}
              </button>

              {showSafetyCheck && (
                <div style={{ marginTop: 8, padding: 12, border: "1px solid #c9d6e8", borderRadius: 4, background: "#f9fafc" }}>
                  <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 8 }}>
                    Enter the medication you're considering prescribing. The safety engine checks against the patient's current medications, allergies, age, and active diagnoses. Bedrock Claude layers context-aware concerns on top of structured rules.
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                    <input
                      placeholder="Drug name (e.g., ibuprofen)"
                      value={safetyForm.drugName}
                      onChange={(e) => setSafetyForm({ ...safetyForm, drugName: e.target.value })}
                      style={{ padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
                    />
                    <input
                      placeholder="Dosage (e.g., 400 mg)"
                      value={safetyForm.dosage}
                      onChange={(e) => setSafetyForm({ ...safetyForm, dosage: e.target.value })}
                      style={{ padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
                    />
                    <input
                      placeholder="Frequency (e.g., QID)"
                      value={safetyForm.frequency}
                      onChange={(e) => setSafetyForm({ ...safetyForm, frequency: e.target.value })}
                      style={{ padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
                    />
                    <select
                      value={safetyForm.route}
                      onChange={(e) => setSafetyForm({ ...safetyForm, route: e.target.value })}
                      style={{ padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
                    >
                      <option value="oral">oral</option>
                      <option value="IV">IV</option>
                      <option value="IM">IM</option>
                      <option value="topical">topical</option>
                      <option value="inhaled">inhaled</option>
                      <option value="rectal">rectal</option>
                      <option value="subcutaneous">subcutaneous</option>
                    </select>
                  </div>
                  <button
                    onClick={() => setSafetyProposed({
                      drugName: safetyForm.drugName.trim(),
                      dosage: safetyForm.dosage.trim(),
                      frequency: safetyForm.frequency.trim(),
                      route: safetyForm.route,
                    })}
                    disabled={!safetyForm.drugName.trim()}
                    className="btn-primary"
                    style={{ fontSize: 12 }}
                  >
                    Run contraindication check
                  </button>
                  {safetyProposed && note && (
                    <div style={{ marginTop: 10 }}>
                      <MedicationSafetyPanel
                        patientId={note.patient_id}
                        proposed={safetyProposed}
                        clinicalNoteId={note.id}
                        encounterId={note.encounter_id}
                        autoRun={true}
                        onDispositionRecorded={() => {
                          // Refresh the note in case it was tied to the prescription
                          // — clear the proposed so the panel can re-run if needed
                          setSafetyProposed(null);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeSectionKey && (
            <SectionEditor
              sectionKey={activeSectionKey}
              label={(template.section_labels && template.section_labels[activeSectionKey]) || activeSectionKey}
              value={sections[activeSectionKey] || ""}
              onChange={(v) => updateSection(activeSectionKey, v)}
              readOnly={!isDraft || !canEdit}
              /* ⭐ P193 — live again, on our own transcription service. Still
                 null (and the button inert-but-labelled) when the device
                 cannot record, so there is never a dead control. */
              onStartDictation={noteMic.available ? (() => startDictation(activeSectionKey)) : null}
              onCursor={(pos) => { cursorRef.current = pos; }}
              micState={noteMic.state}
              micElapsed={noteMic.elapsed}
              onReDictate={reDictateSection}
              dictating={dictating && dictationTargetSection === activeSectionKey}
              dictationStatus={dictationStatus}
              dictationPaused={dictationPaused}
              dictationBuffer={dictationBuffer}
              onPauseDictation={pauseDictation}
              onResumeDictation={resumeDictation}
              onStopAndInsert={stopAndInsert}
              onCancelDictation={cancelDictation}
              onInsertBuffer={insertDictationIntoSection}
              onAiScribe={runAiScribe}
              aiScribing={aiScribing}
              smartPhrases={smartPhrases}
            />
          )}

          {/* ── ICD-10 / CPT panel ──────────────────────────────── */}
          <div style={{ marginTop: 16, padding: 12, border: "1px solid #e1e6ed", borderRadius: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Billing Codes (ICD-10 / CPT)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <CodeList
                label="ICD-10 (diagnosis)"
                codes={icd10Codes}
                onAdd={addIcd}
                onRemove={(c) => setIcd10Codes(icd10Codes.filter((x) => x !== c))}
                newValue={newIcd} setNewValue={setNewIcd}
                placeholder="e.g., E11.9"
                readOnly={!isDraft || !canEdit}
              />
              <CodeList
                label="CPT (procedure)"
                codes={cptCodes}
                onAdd={addCpt}
                onRemove={(c) => setCptCodes(cptCodes.filter((x) => x !== c))}
                newValue={newCpt} setNewValue={setNewCpt}
                placeholder="e.g., 99213"
                readOnly={!isDraft || !canEdit}
              />
            </div>
          </div>

          {/* ── Addenda list ────────────────────────────────────── */}
          {addenda.length > 0 && (
            <div style={{ marginTop: 16, padding: 12, border: "1px solid #d6c9e8", borderRadius: 4, background: "#fbf8ff" }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: "#6b1f8b" }}>
                Addenda ({addenda.length})
              </div>
              {addenda.map((a) => (
                <div key={a.id} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #e0d6ef" }}>
                  <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 4 }}>
                    {a.signed_by_display_name} · {new Date(a.signed_at).toLocaleString()}
                    {a.reason && <span> · reason: {a.reason}</span>}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{a.addendum_text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── AI Scribe review modal ──────────────────────────────── */}
      {showAiReviewModal && aiResult && (
        <div style={modalBackdrop} onClick={() => setShowAiReviewModal(false)}>
          <div style={{ ...modalCard, width: 700, maxWidth: "92vw" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Sparkles size={16} color="#1f6feb" />
              <h2 style={{ fontSize: 16, margin: 0 }}>Review AI-structured draft</h2>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 10,
                background: aiResult.ai_confidence === "high" ? "#e9f5e9" : aiResult.ai_confidence === "medium" ? "#fef6e0" : "#fde8e8",
                color: aiResult.ai_confidence === "high" ? "#2d6b2d" : aiResult.ai_confidence === "medium" ? "#7c5210" : "#8b0000",
                textTransform: "uppercase", letterSpacing: 0.4,
              }}>AI Confidence: {aiResult.ai_confidence}</span>
            </div>
            <p style={{ fontSize: 12, color: "#5a6478", marginTop: 0 }}>
              AI is listening and structuring your dictation into {template.name} format. Approve, edit, or re-dictate.
              <br />
              <em>AI-generated content must be reviewed and edited by a licensed provider before finalization.</em>
            </p>
            {aiResult.ai_notes && (
              <div style={{ fontSize: 12, padding: 8, background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 3, marginBottom: 10 }}>
                <strong>AI notes:</strong> {aiResult.ai_notes}
              </div>
            )}
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {Object.entries(aiResult.sections || {}).map(([k, v]) => (
                <div key={k} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#5a6478", marginBottom: 3 }}>
                    {(template.section_labels && template.section_labels[k]) || k}
                  </div>
                  <div style={{ fontSize: 12, background: "#f9fafc", padding: 8, borderRadius: 3, border: "1px solid #e1e6ed", whiteSpace: "pre-wrap", minHeight: 24 }}>
                    {v || <span style={{ color: "#9aa4b2", fontStyle: "italic" }}>(no content)</span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button className="btn-ghost" onClick={() => setShowAiReviewModal(false)}>Edit AI Draft</button>
              <button className="btn-primary" onClick={acceptAiResult}>
                <CheckCircle2 size={13} /> Accept AI Suggestions
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Addendum modal ──────────────────────────────────────── */}
      {showAddendumModal && (
        <div style={modalBackdrop} onClick={() => setShowAddendumModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, marginTop: 0 }}>Add late entry or correction</h2>
            <p style={{ fontSize: 12, color: "#5a6478" }}>
              The original signed note is preserved per HIPAA §164.530(j). Your addendum
              will be appended and e-signed separately.
            </p>
            <label style={{ fontSize: 11, color: "#5a6478", display: "block", marginTop: 8 }}>Addendum text *</label>
            <textarea
              value={addendumText}
              onChange={(e) => setAddendumText(e.target.value)}
              rows={6}
              style={{ width: "100%", padding: 8, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
            />
            <label style={{ fontSize: 11, color: "#5a6478", display: "block", marginTop: 8 }}>Reason (optional)</label>
            <input
              value={addendumReason}
              onChange={(e) => setAddendumReason(e.target.value)}
              style={{ width: "100%", padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
              placeholder="e.g., late lab result became available"
            />
            <div style={{ marginTop: 12, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button className="btn-ghost" onClick={() => setShowAddendumModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={submitAddendum}>Sign &amp; Add</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Route for Review modal ──────────────────────────────── */}
      {showRouteModal && (
        <div style={modalBackdrop} onClick={() => setShowRouteModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ fontSize: 16, marginTop: 0 }}>Route for Review</h2>
            <p style={{ fontSize: 12, color: "#5a6478" }}>
              Forward this note to a supervising physician or billing. The recipient will see it in their queue.
            </p>
            <label style={{ fontSize: 11, color: "#5a6478", display: "block", marginTop: 8 }}>Reviewer *</label>
            <select
              value={routeTargetUserId}
              onChange={(e) => setRouteTargetUserId(e.target.value)}
              style={{ width: "100%", padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
            >
              <option value="">— pick a reviewer —</option>
              {reviewers.map((u) => (
                <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>
              ))}
            </select>
            <label style={{ fontSize: 11, color: "#5a6478", display: "block", marginTop: 8 }}>Notes (optional)</label>
            <textarea
              value={routeNotes}
              onChange={(e) => setRouteNotes(e.target.value)}
              rows={3}
              placeholder="e.g., Pending attending sign-off — patient discharged."
              style={{ width: "100%", padding: 6, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
            />
            <div style={{ marginTop: 12, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 6 }}>
              <button className="btn-ghost" onClick={() => setShowRouteModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={submitRoute} disabled={!routeTargetUserId}>
                <UserCheck size={13} /> Route
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={complianceFooter}>
        All entries are time-stamped and audited per HIPAA requirements. Auto-save every 30 seconds.
      </div>
    </div>
  );
}

// ─── Section editor with dictation controls ──────────────────────────────
function SectionEditor({
  sectionKey, label, value, onChange, readOnly,
  onStartDictation, onReDictate,
  dictating, dictationStatus, dictationPaused, dictationBuffer,
  onPauseDictation, onResumeDictation, onStopAndInsert, onCancelDictation,
  onInsertBuffer, onAiScribe, aiScribing, smartPhrases, onCursor, micState, micElapsed,
}) {
  return (
    <div style={{ border: "1px solid #e1e6ed", borderRadius: 4, padding: 12, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{label}</h3>
        {!readOnly && !dictating && (
          <div style={{ display: "flex", gap: 6 }}>
            {/*
              ⛔ NO DEAD BUTTON (Card P183). Disabled and relabelled rather
              than removed, so a clinician sees where dictation went. No
              onClick at all — a disabled control that still wires a handler is
              one style change away from being live again.
            */}
            <button
              onClick={onStartDictation || undefined}
              disabled={!onStartDictation || micState === 'transcribing'}
              aria-pressed={micState === 'recording'}
              className="btn-ghost"
              data-testid="note-mic"
              title={onStartDictation
                ? "Dictate into this section — audio goes to our own transcription service"
                : "Dictation is unavailable on this device"}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
                       opacity: onStartDictation ? 1 : 0.55,
                       cursor: onStartDictation ? "pointer" : "not-allowed" }}
            >
              <Mic size={13} /> {micState === 'recording' ? `Stop (${micElapsed}s)`
                : micState === 'transcribing' ? 'Transcribing…' : 'Dictate'}
            </button>
            {/* ⛔ Card P183: Re-Dictate is hidden while dictation is paused. It
                cleared the section before re-recording, so leaving it visible
                would offer to delete a clinician's text and then not record. */}
            {false && onReDictate && value && value.trim().length > 0 && (
              <button onClick={onReDictate} className="btn-ghost" title="Clear this section and re-dictate from scratch" style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#7c5210" }}>
                <RefreshCcw size={13} /> Re-Dictate Section
              </button>
            )}
          </div>
        )}
      </div>

      {!readOnly && dictating && (
        <div style={{ marginBottom: 10, padding: 10, background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 4 }}>
          <div style={{ fontSize: 12, color: "#5a6478", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            {dictationStatus === "listening" && <><Mic size={12} color="#b00020" /> <strong>Listening…</strong> Speak now. Your words will be transcribed in real time.</>}
            {dictationStatus === "processing" && <><Loader2 size={12} className="spin" /> Processing…</>}
            {dictationStatus === "complete" && <><CheckCircle2 size={12} color="#2d6b2d" /> Transcription complete. Review before inserting into note.</>}
          </div>
          <div style={{ fontSize: 12, padding: 8, background: "#fff", border: "1px solid #e1e6ed", borderRadius: 3, minHeight: 60, whiteSpace: "pre-wrap" }}>
            {dictationBuffer || <em style={{ color: "#9aa4b2" }}>Say "new paragraph" or "next section" to organize content…</em>}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
            {!dictationPaused && dictationStatus === "listening" && (
              <button onClick={onPauseDictation} className="btn-ghost" style={{ fontSize: 12 }}>
                <Pause size={12} /> Pause Dictation
              </button>
            )}
            {dictationPaused && (
              <button onClick={onResumeDictation} className="btn-ghost" style={{ fontSize: 12 }}>
                <Play size={12} /> Resume Dictation
              </button>
            )}
            <button onClick={onStopAndInsert} className="btn-ghost" style={{ fontSize: 12 }}>
              <MicOff size={12} /> Stop
            </button>
            <button onClick={onInsertBuffer} disabled={!dictationBuffer} className="btn-primary" style={{ fontSize: 12 }}>
              Insert into "{label}"
            </button>
            <button onClick={onAiScribe} disabled={!dictationBuffer || aiScribing} className="btn-primary" style={{ fontSize: 12, background: "#6b1f8b" }}>
              {aiScribing ? <><Loader2 size={12} className="spin" /> AI structuring…</> : <><Sparkles size={12} /> Generate Draft with AI</>}
            </button>
            <button onClick={onCancelDictation} className="btn-ghost" style={{ fontSize: 12, color: "#b00020" }}>
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      )}

      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        /*
         * ⭐ P193 — report the caret so a dictated transcript lands WHERE THE
         * CLINICIAN IS, not always at the end. onSelect fires for clicks,
         * arrow keys and drags alike; onBlur keeps the last position when the
         * textarea loses focus to the mic button, which is exactly when it
         * matters.
         */
        onSelect={(e) => onCursor && onCursor(e.target.selectionStart)}
        onBlur={(e) => onCursor && onCursor(e.target.selectionStart)}
        data-testid={`note-section-${sectionKey}`}
        rows={12}
        readOnly={readOnly}
        placeholder={readOnly ? "(no content)" : `Type or dictate ${label.toLowerCase()}…`}
        style={{
          width: "100%", padding: 10, fontSize: 13, fontFamily: "inherit",
          border: "1px solid #c9d6e8", borderRadius: 3, resize: "vertical",
          background: readOnly ? "#f9fafc" : "#fff",
        }}
      />

      {!readOnly && smartPhrases && smartPhrases.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#5a6478" }}>
          Smart phrases: {smartPhrases.slice(0, 6).map((p) => (
            <span key={p.id} style={{ fontFamily: "monospace", marginRight: 8 }}>{p.trigger}</span>
          ))}
          <span style={{ color: "#9aa4b2", fontStyle: "italic" }}>(type the trigger and it expands)</span>
        </div>
      )}

      <div style={{ marginTop: 4, fontSize: 10, color: "#9aa4b2", fontStyle: "italic" }}>
        Dictation Tip: speak clearly in complete sentences. Say "new paragraph" or "next section" to organize content.
      </div>
    </div>
  );
}

// ─── Smart-phrase expansion ──────────────────────────────────────────────
function expandSmartPhrases(text, phrases) {
  if (!text || !phrases || phrases.length === 0) return text;
  let out = text;
  for (const p of phrases) {
    if (!p.trigger) continue;
    // Match the trigger followed by a word boundary (space, newline, punctuation, end of string)
    const re = new RegExp(escapeRegex(p.trigger) + "(?=\\s|$|[.,;:!?])", "g");
    out = out.replace(re, p.phrase_text);
  }
  return out;
}
function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// ─── ICD-10 / CPT mini-component ─────────────────────────────────────────
function CodeList({ label, codes, onAdd, onRemove, newValue, setNewValue, placeholder, readOnly }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 4 }}>{label}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {(codes || []).map((c) => (
          <span key={c} style={{
            padding: "2px 8px", fontSize: 11, fontFamily: "monospace",
            background: "#e8f0fb", border: "1px solid #c9d6e8", borderRadius: 10,
            display: "inline-flex", alignItems: "center", gap: 4,
          }}>
            {c}
            {!readOnly && (
              <button onClick={() => onRemove(c)} style={{ background: "transparent", border: "none", color: "#5a6478", cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
            )}
          </span>
        ))}
      </div>
      {!readOnly && (
        <div style={{ display: "flex", gap: 4 }}>
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
            placeholder={placeholder}
            style={{ flex: 1, padding: 5, fontSize: 12, border: "1px solid #c9d6e8", borderRadius: 3 }}
          />
          <button onClick={onAdd} className="btn-ghost" style={{ fontSize: 12 }}>
            <Plus size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────
const statusBadge = {
  display: "inline-block", padding: "3px 10px", borderRadius: 10, fontSize: 11,
  fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
};
const statusBar = {
  padding: "6px 10px", fontSize: 12, borderRadius: 3, marginBottom: 10,
  display: "flex", alignItems: "center", gap: 6,
};
const tooltipBar = {
  fontSize: 12, color: "#5a6478", marginBottom: 12, padding: "8px 12px",
  background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 3,
};
const modalBackdrop = {
  position: "fixed", inset: 0, background: "rgba(15, 22, 36, 0.4)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
};
const modalCard = {
  background: "#fff", padding: 20, borderRadius: 6, width: 520, maxWidth: "90vw",
  boxShadow: "0 6px 24px rgba(0, 0, 0, 0.15)",
};
const complianceFooter = {
  marginTop: 18, padding: "8px 12px", fontSize: 10, color: "#9aa4b2",
  textAlign: "center",
};

function statusBarColor(kind) {
  if (kind === "saving") return { background: "#f4f7fb", color: "#5a6478", border: "1px solid #c9d6e8" };
  if (kind === "ok") return { background: "#e9f5e9", color: "#2d6b2d", border: "1px solid #a5d5a5" };
  if (kind === "error") return { background: "#fde8e8", color: "#8b0000", border: "1px solid #f5a5a5" };
  return {};
}

// ─── Print-friendly HTML rendering ──────────────────────────────────────
// Builds a clean, print-styled HTML document of the note. Opened in a
// new window, then window.print() triggers the browser print dialog
// (where "Save as PDF" is available for client-side export).
function buildPrintHtml({ note, template, sections, icd10Codes, cptCodes, addenda }) {
  const esc = (s) => String(s == null ? "" : s)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const sectionHtml = (template.section_keys || [])
    .filter((k) => sections[k] && String(sections[k]).trim())
    .map((k) => {
      const label = (template.section_labels && template.section_labels[k]) || k;
      return `<section><h2>${esc(label)}</h2><p>${esc(sections[k]).replace(/\n/g, "<br>")}</p></section>`;
    }).join("\n");
  const billing = (icd10Codes && icd10Codes.length) || (cptCodes && cptCodes.length)
    ? `<section class="billing"><h2>Billing Codes</h2>
         ${icd10Codes && icd10Codes.length ? `<div><strong>ICD-10:</strong> ${esc(icd10Codes.join(", "))}</div>` : ""}
         ${cptCodes && cptCodes.length ? `<div><strong>CPT:</strong> ${esc(cptCodes.join(", "))}</div>` : ""}
       </section>` : "";
  const addendaHtml = (addenda && addenda.length > 0)
    ? `<section class="addenda"><h2>Addenda (${addenda.length})</h2>
         ${addenda.map((a) => `
           <div class="addendum">
             <div class="meta">${esc(a.signed_by_display_name)} · ${esc(new Date(a.signed_at).toLocaleString())}${a.reason ? " · reason: " + esc(a.reason) : ""}</div>
             <div class="text">${esc(a.addendum_text).replace(/\n/g, "<br>")}</div>
           </div>`).join("")}
       </section>` : "";
  return `<!doctype html>
<html><head>
<meta charset="utf-8">
<title>${esc(template.name)} — Note ${esc(note.id)}</title>
<script>window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 250); });</script>
<style>
  @page { size: letter; margin: 0.75in; }
  body { font-family: -apple-system, BlinkMacSystemFont, Helvetica, Arial, sans-serif; color: #1f2a3b; line-height: 1.45; }
  header { border-bottom: 1px solid #c9d6e8; padding-bottom: 12px; margin-bottom: 18px; }
  .brand { font-size: 9pt; color: #5a6478; letter-spacing: 0.5px; text-transform: uppercase; }
  h1 { font-size: 18pt; margin: 4px 0 12px 0; color: #0b2240; }
  .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; font-size: 10pt; color: #3a4a5e; }
  h2 { font-size: 12pt; margin: 16px 0 6px 0; color: #0b2240; border-bottom: 1px solid #e1e6ed; padding-bottom: 3px; }
  section { margin-bottom: 14px; page-break-inside: avoid; }
  section p { white-space: pre-wrap; font-size: 11pt; }
  .billing { background: #f4f7fb; padding: 10px 14px; border-radius: 3px; }
  .billing div { font-size: 10pt; margin: 2px 0; }
  .addenda { border-top: 2px solid #d6c9e8; margin-top: 24px; padding-top: 12px; }
  .addendum { border-left: 3px solid #6b1f8b; padding-left: 10px; margin: 8px 0; }
  .addendum .meta { font-size: 9pt; color: #5a6478; font-style: italic; }
  .addendum .text { font-size: 11pt; margin-top: 3px; }
  footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e1e6ed; font-size: 8pt; color: #5a6478; }
  .hash { font-family: 'SF Mono', Menlo, monospace; font-size: 7pt; word-break: break-all; }
  @media print { button { display: none; } }
</style>
</head><body>
<header>
  <div class="brand">ZIGNAGENIX MEDICAL INTELLIGENCE PLATFORM</div>
  <h1>${esc(template.name)}</h1>
  <div class="meta-grid">
    <div><strong>Patient:</strong> ${esc(note.patient_first || "")} ${esc(note.patient_last || "")} ${note.patient_first ? "" : "(#" + note.patient_id + ")"}</div>
    <div><strong>MRN:</strong> ${esc(note.patient_mrn || "—")}</div>
    <div><strong>DOB:</strong> ${esc(note.patient_dob ? new Date(note.patient_dob).toISOString().slice(0,10) : "—")}</div>
    <div><strong>Note ID:</strong> ${esc(note.id)}</div>
    <div><strong>Author:</strong> ${esc(note.author_display_name || "User " + note.author_user_id)}</div>
    <div><strong>Created:</strong> ${esc(new Date(note.created_at).toLocaleString())}</div>
    <div><strong>Status:</strong> ${esc(note.status.toUpperCase())}</div>
    <div>${note.signed_at ? "<strong>Signed:</strong> " + esc(new Date(note.signed_at).toLocaleString()) + " by " + esc(note.signed_by_display_name || "") : ""}</div>
  </div>
</header>
${sectionHtml}
${billing}
${addendaHtml}
<footer>
  <div><strong>E-signature provenance</strong></div>
  ${note.signature_hash ? `<div class="hash">Hash: ${esc(note.signature_hash)}</div>` : ""}
  <div style="margin-top: 6px;">All entries are time-stamped and audited per HIPAA §164.312(b). Generated ${new Date().toISOString()}.</div>
</footer>
</body></html>`;
}
